import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, ShieldAlert, ShieldCheck } from "lucide-react";
import KycPanel from "../components/KycPanel";
import TrainingMaterialPanel from "../components/TrainingMaterialPanel";
import VerifiedBadge from "../components/VerifiedBadge";
import PickupDistance from "../components/PickupDistance";
import AddressAutocomplete from "../components/AddressAutocomplete";
import PackageEditDialog from "../components/PackageEditDialog";
import TimeSlotsEditor from "../components/TimeSlotsEditor";
import Seo from "../components/Seo";
import { PROVINCES } from "../lib/provinces";

const DAYS = ["mon","tue","wed","thu","fri","sat","sun"];
const TRANS = ["manual","auto"];
const CARS = ["sedan","suv","hatchback"];
const EQUIPMENT = ["Cars","Motorcycles","Buses","Trucks","Trailers","Commercial"];
const EQ_TRANS = ["auto","manual"];

export default function TrainerDashboard() {
  const [zones, setZones] = useState([]);
  const [tab, setTab] = useState("bookings");
  const [profile, setProfile] = useState({
    zone: "", bio: "", hourly_rate: 40, transmissions: ["auto"],
    car_types: ["sedan"], availability: ["mon","tue","wed","thu","fri"],
    years_experience: 1, car_model: "", photo_url: "",
    base_address: "",
    equipment: ["Cars"], equipment_transmissions: { Cars: ["auto"] },
    overtime_rate_pct: 25, certifications: [], languages: ["en"],
    time_slots: { mon: [{start:"09:00", end:"17:00"}], tue: [{start:"09:00", end:"17:00"}],
                  wed: [{start:"09:00", end:"17:00"}], thu: [{start:"09:00", end:"17:00"}],
                  fri: [{start:"09:00", end:"17:00"}], sat: [], sun: [] },
    slot_minutes: 60,
    stripe_account_id: "",
  });
  const [bookings, setBookings] = useState([]);
  const [packages, setPackages] = useState([]);
  const [editingPkg, setEditingPkg] = useState(null);
  const [newPkg, setNewPkg] = useState({ title: "", description: "", hours: 10, price: 350, discount_percent: 0 });
  const [bookingFilter, setBookingFilter] = useState("");
  // Memoised: re-derived on every render otherwise, including each keystroke.
  const visibleBookings = useMemo(() => bookings.filter(b => {
    const q = bookingFilter.trim().toLowerCase();
    return !q || [b.learner_name, b.status, b.transmission]
      .some(v => (v || "").toLowerCase().includes(q));
  }), [bookings, bookingFilter]);
  const [zoneReq, setZoneReq] = useState({ name: "", city: "", province: "" });
  const [zoneReqMsg, setZoneReqMsg] = useState("");

  const loadAll = useCallback(async () => {
    const [z, p, b, pk] = await Promise.all([
      api.get("/zones"), api.get("/trainer/profile"),
      api.get("/trainer/bookings"), api.get("/trainer/packages"),
    ]);
    setZones(z.data);
    if (p.data && p.data.zone) setProfile(prev => ({ ...prev, ...p.data }));
    setBookings(b.data);
    setPackages(pk.data);
  }, []);
  useEffect(() => { loadAll(); }, [loadAll]);

  const submitZoneRequest = async () => {
    if (!zoneReq.name.trim() || !zoneReq.city.trim()) {
      toast.error("Both zone name and city are required");
      return;
    }
    if (!zoneReq.province) {
      toast.error("Pick the province — it decides the sales tax on lessons in this zone");
      return;
    }
    try {
      const r = await api.post("/trainer/zones", { name: zoneReq.name.trim(), city: zoneReq.city.trim(), province: zoneReq.province });
      setZoneReqMsg(r.data.message || "Submitted for admin approval");
      toast.success(r.data.message || "Submitted");
      setZoneReq({ name: "", city: "", province: "" });
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not submit zone");
    }
  };

  const toggle = (key, val) => setProfile(p => ({ ...p, [key]: p[key].includes(val) ? p[key].filter(x => x !== val) : [...p[key], val] }));

  const toggleEquipment = (eq) => setProfile(p => {
    const has = p.equipment.includes(eq);
    const next = has ? p.equipment.filter(x => x !== eq) : [...p.equipment, eq];
    const nextET = { ...(p.equipment_transmissions || {}) };
    if (has) { delete nextET[eq]; } else if (!nextET[eq]) { nextET[eq] = ["auto"]; }
    return { ...p, equipment: next, equipment_transmissions: nextET };
  });

  const toggleEquipmentTrans = (eq, tr) => setProfile(p => {
    const cur = (p.equipment_transmissions || {})[eq] || [];
    const next = cur.includes(tr) ? cur.filter(x => x !== tr) : [...cur, tr];
    return { ...p, equipment_transmissions: { ...(p.equipment_transmissions || {}), [eq]: next } };
  });

  const saveProfile = async () => {
    try {
      await api.put("/trainer/profile", { ...profile, hourly_rate: Number(profile.hourly_rate), years_experience: Number(profile.years_experience) });
      toast.success("Profile saved");
      loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };

  const uploadSelfie = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 3 * 1024 * 1024) {
      toast.error(`Selfie too large — max 3 MB. This file is ${(f.size / 1024 / 1024).toFixed(2)} MB.`);
      e.target.value = "";
      return;
    }
    if (!/^image\/(jpe?g|png|webp)$/i.test(f.type)) {
      toast.error("Please upload a JPG, PNG or WEBP image");
      e.target.value = "";
      return;
    }
    const fd = new FormData();
    fd.append("file", f);
    try {
      const { data } = await api.post("/trainer/profile/selfie", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setProfile((p) => ({ ...p, photo_url: data.photo_url }));
      toast.success("Selfie updated");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    }
    e.target.value = "";
  };

  const addPkg = async () => {
    try {
      await api.post("/trainer/packages", { ...newPkg, hours: Number(newPkg.hours), price: Number(newPkg.price), discount_percent: Number(newPkg.discount_percent) || 0 });
      setNewPkg({ title: "", description: "", hours: 10, price: 350, discount_percent: 0 });
      loadAll();
    } catch (e) { toast.error("Could not add package"); }
  };
  const delPkg = async (id) => { await api.delete(`/trainer/packages/${id}`); loadAll(); };
  const savePkg = async (fields) => {
    try {
      await api.put(`/trainer/packages/${editingPkg.package_id}`, fields);
      toast.success("Package updated");
      setEditingPkg(null);
      loadAll();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not update package");
    }
  };

  // Only settled overtime counts — pending/declined overtime is not money the
  // platform has collected.
  const earnings = bookings
    .filter(b => b.payment_status === "paid")
    .reduce((s, b) => s + b.amount + (b.overtime_status === "settled" ? (b.overtime_amount || 0) : 0), 0);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 sm:py-12" data-testid="trainer-dashboard">
      <Seo title="Trainer Hub – DriveTerrain" description="Your DriveTerrain trainer dashboard." canonical="/trainer" robots="noindex, nofollow" hrefLangs={false} />
      <div className="mb-8">
        <div className="text-xs uppercase tracking-[0.2em] font-bold text-primary">Trainer Hub</div>
        <h1 className="text-4xl font-display font-semibold tracking-tight mt-1 flex flex-wrap items-center gap-3">
          Manage your practice
          {profile.kyc_status === "verified" && <VerifiedBadge size="md" testId="trainer-verified-badge" />}
        </h1>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 mb-8">
        {[
          { l: "Bookings", v: bookings.length },
          { l: "Approved", v: profile.approved ? "Yes" : "Pending" },
          { l: "Sessions done", v: profile.sessions_completed || 0 },
          { l: "Gross earnings", v: `$${earnings.toFixed(2)}` },
        ].map((s) => (
          <div key={s.l} className="border border-border bg-card p-4 sm:p-5 min-w-0">
            <div className="text-xs uppercase tracking-widest text-muted-foreground">{s.l}</div>
            <div className="font-display text-2xl sm:text-3xl font-semibold mt-1 truncate">{s.v}</div>
          </div>
        ))}
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="rounded-sm max-w-full overflow-x-auto no-scrollbar justify-start">
          <TabsTrigger value="bookings" data-testid="tab-bookings">Bookings</TabsTrigger>
          <TabsTrigger value="profile" data-testid="tab-profile">Profile & Pricing</TabsTrigger>
          <TabsTrigger value="kyc" data-testid="tab-kyc">KYC & Verification</TabsTrigger>
          <TabsTrigger value="packages" data-testid="tab-packages">Packages</TabsTrigger>
          <TabsTrigger value="payouts" data-testid="tab-payouts">Payouts</TabsTrigger>
        </TabsList>

        <TabsContent value="bookings" className="mt-6">
          {bookings.length === 0 ? (
            <div className="border border-border bg-card p-12 text-center text-muted-foreground">No bookings yet. Complete your profile to appear in search results.</div>
          ) : (
            <>
              <div className="mb-3"><Input placeholder="Filter by learner name, status or transmission…" value={bookingFilter} onChange={e => setBookingFilter(e.target.value)} className="rounded-sm max-w-md" data-testid="t-filter-bookings" /></div>
              <div className="border border-border bg-card">
                <div className="hidden md:grid md:grid-cols-6 px-5 py-3 text-xs uppercase tracking-widest text-muted-foreground border-b border-border">
                  <div className="md:col-span-2">Learner</div><div>Hours</div><div>Amount</div><div>Status</div><div className="text-right">Action</div>
                </div>
              {visibleBookings.map(b => (
                <div key={b.booking_id} className="grid grid-cols-1 gap-2 md:grid-cols-6 md:gap-0 px-4 sm:px-5 py-4 md:items-start border-b border-border last:border-0 text-sm" data-testid={`t-booking-${b.booking_id}`}>
                  <div className="md:col-span-2">
                    <div className="font-medium">{b.learner_name}</div>
                    {b.learner_kyc_status !== "verified" ? (
                      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-muted-foreground border border-border px-1.5 py-0.5 mt-1" title="This learner has not completed ID verification" data-testid={`learner-unverified-${b.booking_id}`}>
                        <ShieldAlert className="w-3 h-3" /> ID unverified
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-primary border border-primary/40 px-1.5 py-0.5 mt-1" data-testid={`learner-verified-${b.booking_id}`}>
                        <ShieldCheck className="w-3 h-3" /> ID verified
                      </span>
                    )}
                    <div className="text-xs text-muted-foreground mt-0.5">
                      {new Date(b.created_at).toLocaleString()}
                      {b.scheduled_at && <> · {new Date(b.scheduled_at).toLocaleString()}</>}
                    </div>
                    <div className="mt-1">
                      <PickupDistance distance={b.pickup_distance}
                                      onAddBase={() => setTab("profile")}
                                      testId={`pickup-distance-${b.booking_id}`} />
                    </div>
                  </div>
                  <div>{b.hours}h</div>
                  <div>${b.amount.toFixed(2)}</div>
                  <div><span className="px-2 py-0.5 text-xs uppercase tracking-widest border border-border">{b.status}</span></div>
                  <div className="text-right">{b.payment_status === "paid" && b.status !== "completed" && <Link to={`/session/${b.booking_id}`}><Button size="sm" className="rounded-sm">Open session</Button></Link>}</div>
                </div>
              ))}
              </div>
            </>
          )}
        </TabsContent>

        <TabsContent value="profile" className="mt-6">
          <div className="border border-border bg-card p-6 space-y-5 max-w-3xl">
            <div className="flex items-center gap-4 pb-4 border-b border-border" data-testid="profile-selfie-block">
              <div className="w-20 h-20 rounded-full overflow-hidden border border-border bg-muted flex items-center justify-center flex-shrink-0">
                {profile.photo_url ? (
                  <img src={profile.photo_url} alt="Trainer selfie" className="w-full h-full object-cover" data-testid="profile-selfie-preview" />
                ) : (
                  <span className="text-xs text-muted-foreground text-center px-1">No photo</span>
                )}
              </div>
              <div className="flex-1">
                <Label>Profile selfie</Label>
                <p className="text-xs text-muted-foreground mt-0.5">Shown to learners in search results. JPG/PNG/WEBP, up to <strong>3 MB</strong>.</p>
                <label className="inline-block mt-2">
                  <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={uploadSelfie} data-testid="profile-selfie-input" />
                  <Button type="button" variant="outline" size="sm" className="rounded-sm" asChild>
                    <span>{profile.photo_url ? "Replace selfie" : "Upload selfie"}</span>
                  </Button>
                </label>
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label>Zone</Label>
                <select value={profile.zone} onChange={e => setProfile({ ...profile, zone: e.target.value })} className="w-full border border-border bg-background rounded-sm h-10 px-3 text-sm mt-1" data-testid="profile-zone">
                  <option value="">Select…</option>
                  {zones.map(z => <option key={z.zone_id} value={z.name}>{z.name} · {z.city}</option>)}
                </select>
                <details className="mt-2 text-xs" data-testid="zone-request-details">
                  <summary className="cursor-pointer text-primary hover:underline">Don&apos;t see your area? Request a new zone</summary>
                  <div className="grid grid-cols-2 gap-2 mt-2">
                    <Input placeholder="Zone name (e.g. Riverdale)" value={zoneReq.name} onChange={e => setZoneReq({ ...zoneReq, name: e.target.value })} className="rounded-sm" data-testid="zone-req-name" />
                    <Input placeholder="City (e.g. Toronto)" value={zoneReq.city} onChange={e => setZoneReq({ ...zoneReq, city: e.target.value })} className="rounded-sm" data-testid="zone-req-city" />
                  </div>
                  <select value={zoneReq.province} onChange={e => setZoneReq({ ...zoneReq, province: e.target.value })} className="w-full border border-border bg-background rounded-sm h-10 px-3 text-sm mt-2" data-testid="zone-req-province">
                    <option value="">Province (sets the sales tax)…</option>
                    {PROVINCES.map(p => <option key={p.code} value={p.code}>{p.code} · {p.name}</option>)}
                  </select>
                  <Button type="button" size="sm" className="rounded-sm mt-2" onClick={submitZoneRequest} data-testid="submit-zone-request">Submit for approval</Button>
                  {zoneReqMsg && <div className="mt-2 text-muted-foreground" data-testid="zone-req-msg">{zoneReqMsg}</div>}
                </details>
              </div>
              <div>
                <Label>Hourly rate (CAD)</Label>
                <Input type="number" value={profile.hourly_rate} onChange={e => setProfile({ ...profile, hourly_rate: e.target.value })} className="rounded-sm" data-testid="profile-rate" />
              </div>
              <div className="md:col-span-2">
                <Label>Base / home address</Label>
                <AddressAutocomplete value={profile.base_address || ""}
                  onChange={(v) => setProfile(prev => ({ ...prev, base_address: v }))}
                  placeholder="e.g. 120 Front St W, Toronto, ON M5J 2M5"
                  className="rounded-sm" testId="profile-base-address" />
                <div className="text-xs text-muted-foreground mt-1.5">
                  Used only to show you the driving distance to each learner's pickup. Never shown to learners — include the city and postal code so it resolves accurately.
                </div>
              </div>
              <div>
                <Label>Years experience</Label>
                <Input type="number" value={profile.years_experience} onChange={e => setProfile({ ...profile, years_experience: e.target.value })} className="rounded-sm" />
              </div>
              <div>
                <Label>Car model</Label>
                <Input value={profile.car_model} onChange={e => setProfile({ ...profile, car_model: e.target.value })} className="rounded-sm" placeholder="e.g. Toyota Corolla 2022" />
              </div>
            </div>
            <div>
              <Label>Bio</Label>
              <Textarea value={profile.bio} onChange={e => setProfile({ ...profile, bio: e.target.value })} rows={3} className="rounded-sm" />
            </div>
            <div className="border border-border bg-muted/30 p-4 rounded-sm">
              <Label htmlFor="stripe-account-id">Stripe Account ID (for payouts)</Label>
              <p className="text-xs text-muted-foreground mt-0.5">Required before you can request a payout. Paste your Stripe Connect account ID (starts with <code>acct_</code>) — this is where your earnings are sent.</p>
              <Input
                id="stripe-account-id"
                value={profile.stripe_account_id || ""}
                onChange={e => setProfile({ ...profile, stripe_account_id: e.target.value })}
                placeholder="acct_1AbCdEfGhIjKl…"
                className="rounded-sm mt-2 font-mono text-sm"
                data-testid="stripe-account-id"
              />
              <p className="text-xs text-muted-foreground mt-2">
                Changing this will not redirect a payout you have already requested — that
                request keeps the account it was submitted with.
              </p>
            </div>
            <div>
              <Label>Transmissions supported</Label>
              <div className="flex gap-2 mt-2">{TRANS.map(t => (
                <button key={t} type="button" onClick={() => toggle("transmissions", t)} className={`px-4 py-2 text-sm border rounded-sm ${profile.transmissions.includes(t) ? "bg-primary text-primary-foreground border-primary" : "border-border"}`} data-testid={`trans-${t}`}>{t}</button>
              ))}</div>
            </div>
            <div>
              <Label>Equipment specialization</Label>
              <p className="text-xs text-muted-foreground mt-1">Select all the vehicle types you teach. For each, choose supported transmission(s).</p>
              <div className="flex flex-wrap gap-2 mt-2">{EQUIPMENT.map(e => (
                <button key={e} type="button" onClick={() => toggleEquipment(e)} className={`px-4 py-2 text-sm border rounded-sm ${profile.equipment.includes(e) ? "bg-primary text-primary-foreground border-primary" : "border-border"}`} data-testid={`equipment-${e}`}>{e}</button>
              ))}</div>
              {profile.equipment.length > 0 && (
                <div className="mt-4 space-y-2 border-l-2 border-primary/30 pl-3">
                  {profile.equipment.map(eq => (
                    <div key={eq} className="flex items-center gap-3 text-sm" data-testid={`eqrow-${eq}`}>
                      <span className="font-medium w-28">{eq}</span>
                      {EQ_TRANS.map(tr => {
                        const active = ((profile.equipment_transmissions||{})[eq] || []).includes(tr);
                        return (
                          <button key={tr} type="button" onClick={() => toggleEquipmentTrans(eq, tr)}
                                  className={`px-3 py-1 text-xs border rounded-sm ${active ? "bg-primary/10 border-primary text-primary" : "border-border"}`}
                                  data-testid={`eqtrans-${eq}-${tr}`}>
                            {tr}
                          </button>
                        );
                      })}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label>Overtime premium (%)</Label>
                <Input type="number" value={profile.overtime_rate_pct} onChange={e => setProfile({ ...profile, overtime_rate_pct: e.target.value })} className="rounded-sm" data-testid="overtime-pct" />
                <p className="text-xs text-muted-foreground mt-1">Applied to evenings, weekends, and statutory holidays.</p>
              </div>
              <div>
                <Label>Languages spoken</Label>
                <div className="flex gap-2 mt-1.5">
                  {["en","fr"].map(l => (
                    <button key={l} type="button" onClick={() => toggle("languages", l)} className={`px-4 py-2 text-sm border rounded-sm ${(profile.languages||[]).includes(l) ? "bg-primary text-primary-foreground border-primary" : "border-border"}`} data-testid={`lang-${l}`}>{l === 'en' ? 'English' : 'Français'}</button>
                  ))}
                </div>
              </div>
            </div>
            <div>
              <Label>Car types</Label>
              <div className="flex gap-2 mt-2">{CARS.map(t => (
                <button key={t} type="button" onClick={() => toggle("car_types", t)} className={`px-4 py-2 text-sm border rounded-sm ${profile.car_types.includes(t) ? "bg-primary text-primary-foreground border-primary" : "border-border"}`} data-testid={`car-${t}`}>{t}</button>
              ))}</div>
            </div>
            <div>
              <Label>Availability (days)</Label>
              <div className="flex gap-2 mt-2 flex-wrap">{DAYS.map(d => (
                <button key={d} type="button" onClick={() => toggle("availability", d)} className={`px-4 py-2 text-sm border rounded-sm ${profile.availability.includes(d) ? "bg-primary text-primary-foreground border-primary" : "border-border"}`}>{d}</button>
              ))}</div>
            </div>
            <div>
              <Label>Time-slot availability (precise)</Label>
              <TimeSlotsEditor value={profile.time_slots || {}} onChange={(v) => setProfile(p => ({ ...p, time_slots: v }))} />
            </div>
            <Button className="rounded-sm" onClick={saveProfile} data-testid="save-profile-btn">Save profile</Button>
            {!profile.approved && <div className="text-xs text-muted-foreground">⚠ Your profile is pending admin approval before it appears in search.</div>}
          </div>
          <div className="mt-6 max-w-3xl">
            <TrainingMaterialPanel />
          </div>
        </TabsContent>

        <TabsContent value="kyc" className="mt-6">
          <KycPanel />
        </TabsContent>


        <TabsContent value="packages" className="mt-6 space-y-4">
          <div className="border border-border bg-card p-6 grid md:grid-cols-6 gap-3 items-end max-w-4xl">
            <div className="md:col-span-2"><Label>Title</Label><Input value={newPkg.title} onChange={e => setNewPkg({ ...newPkg, title: e.target.value })} className="rounded-sm" data-testid="pkg-title" /></div>
            <div><Label>Hours</Label><Input type="number" value={newPkg.hours} onChange={e => setNewPkg({ ...newPkg, hours: e.target.value })} className="rounded-sm" data-testid="pkg-hours" /></div>
            <div><Label>Price (CAD)</Label><Input type="number" value={newPkg.price} onChange={e => setNewPkg({ ...newPkg, price: e.target.value })} className="rounded-sm" data-testid="pkg-price" /></div>
            <div><Label>Discount %</Label><Input type="number" min={0} max={90} value={newPkg.discount_percent} onChange={e => setNewPkg({ ...newPkg, discount_percent: e.target.value })} className="rounded-sm" data-testid="pkg-discount" placeholder="0" /></div>
            <Button className="rounded-sm" onClick={addPkg} data-testid="add-pkg-btn"><Plus className="w-4 h-4 mr-2" />Add</Button>
            <div className="md:col-span-6"><Label>Description</Label><Textarea value={newPkg.description} onChange={e => setNewPkg({ ...newPkg, description: e.target.value })} rows={2} className="rounded-sm" data-testid="pkg-desc" /></div>
            <p className="md:col-span-6 text-xs text-muted-foreground">Discount is applied to the final price learners pay at checkout. Learners pick a slot from your <strong>existing working schedule</strong> when booking a package.</p>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            {packages.map(p => (
              <div key={p.package_id} className="border border-border bg-card p-4 sm:p-5 min-w-0" data-testid={`pkg-${p.package_id}`}>
                <div className="flex justify-between items-start">
                  <div>
                    <div className="font-display text-lg font-medium">{p.title}</div>
                    <div className="text-sm text-muted-foreground">
                      {p.hours}h · ${p.price}
                      {p.discount_percent > 0 && (
                        <span className="ml-2 inline-block px-2 py-0.5 bg-primary/10 text-primary text-xs font-medium rounded-sm" data-testid={`pkg-discount-badge-${p.package_id}`}>
                          {p.discount_percent}% off → ${((Number(p.price) || 0) * (1 - Number(p.discount_percent) / 100)).toFixed(2)}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <button onClick={() => setEditingPkg(p)} className="text-muted-foreground hover:text-primary transition-colors" aria-label="Edit package" data-testid={`edit-pkg-${p.package_id}`}><Pencil className="w-4 h-4" /></button>
                    <button onClick={() => delPkg(p.package_id)} className="text-muted-foreground hover:text-destructive transition-colors" aria-label="Delete package" data-testid={`del-${p.package_id}`}><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
                <div className="text-sm mt-2">{p.description}</div>
              </div>
            ))}
          </div>
          {editingPkg && (
            <PackageEditDialog
              pkg={editingPkg}
              onOpenChange={(o) => !o && setEditingPkg(null)}
              onSave={savePkg}
            />
          )}
        </TabsContent>

        <TabsContent value="payouts" className="mt-6">
          <PayoutsPanel />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function PayoutsPanel() {
  const [data, setData] = useState(null);
  const [amount, setAmount] = useState(0);
  const load = useCallback(
    () => api.get("/trainer/payouts").then(r => { setData(r.data); setAmount(r.data.available_balance); }),
    []
  );
  useEffect(() => { load(); }, [load]);
  const request = async () => {
    try {
      await api.post("/trainer/payouts/request", { amount: Number(amount) });
      toast.success("Payout requested");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  if (!data) return <div className="text-muted-foreground">Loading…</div>;
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
        <PayoutStat l="Gross earnings" v={`$${data.gross_earnings.toFixed(2)}`} testid="payout-gross" />
        <PayoutStat l={`Platform fee (${data.commission_percent}%)`} v={`− $${data.commission_amount.toFixed(2)}`} testid="payout-fee" />
        <PayoutStat l="Paid out" v={`$${data.paid_out.toFixed(2)}`} testid="payout-paid" />
        <PayoutStat l="Available" v={`$${data.available_balance.toFixed(2)}`} testid="payout-available" highlight />
      </div>
      <div className="border border-border bg-card p-6 max-w-xl">
        <div className="font-display text-xl font-medium">Request payout</div>
        <p className="text-sm text-muted-foreground mt-1">Admin reviews and marks paid. Pending requests reduce your available balance.</p>
        <div className="flex gap-2 mt-4 items-end">
          <div className="flex-1">
            <Label>Amount (USD)</Label>
            <Input type="number" value={amount} onChange={e => setAmount(e.target.value)} className="rounded-sm" data-testid="payout-amount" />
          </div>
          <Button className="rounded-sm h-10" onClick={request} disabled={data.available_balance <= 0} data-testid="request-payout-btn">Request payout</Button>
        </div>
      </div>
      <div>
        <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Payout history</div>
        {data.payouts.length === 0 ? (
          <div className="border border-border bg-card p-6 text-sm text-muted-foreground" data-testid="no-payouts">No payouts yet.</div>
        ) : (
          <div className="border border-border bg-card">
            <div className="hidden md:grid md:grid-cols-3 px-5 py-3 text-xs uppercase tracking-widest text-muted-foreground border-b border-border">
              <div>Requested</div><div>Amount</div><div>Status</div>
            </div>
            {data.payouts.map(p => (
              <div key={p.payout_id} className="grid grid-cols-1 gap-1.5 md:grid-cols-3 md:gap-0 px-5 py-3 text-sm border-b border-border last:border-0" data-testid={`payout-${p.payout_id}`}>
                <div>{new Date(p.created_at).toLocaleString()}</div>
                <div>${p.amount.toFixed(2)}</div>
                <div><span className={`px-2 py-0.5 text-xs uppercase tracking-widest border ${p.status==='paid'?'border-primary text-primary':p.status==='rejected'?'border-destructive text-destructive':'border-border'}`}>{p.status}</span>
                  {p.status === "rejected" && p.reject_reason && (
                    <div className="text-xs text-destructive mt-1.5" data-testid={`payout-reason-${p.payout_id}`}>
                      {p.reject_reason}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function PayoutStat({ l, v, testid, highlight }) {
  return (
    <div className={`border ${highlight?'border-primary':'border-border'} bg-card p-5`}>
      <div className="text-xs uppercase tracking-widest text-muted-foreground">{l}</div>
      <div className={`font-display text-2xl sm:text-3xl font-semibold mt-1 truncate ${highlight?'text-primary':''}`} data-testid={testid}>{v}</div>
    </div>
  );
}
